import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import { IntlState } from 'react-intl-redux';
import ReactToPrint from 'react-to-print';
import {
  Box,
  Button,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PrintIcon from '@mui/icons-material/Print';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridOnIcon from '@mui/icons-material/GridOn';
import ApiClient from '../loopback/apiClient';
import {
  fetchFullDigitalSurvey,
  setSelectedDigitalsection,
  setSelectedCase,
  addOrUpdateDigitalSubmission,
  fetchInstituteDigitalSubmissions,
  fetchDigitalSubmissionsWithMetas,
  setDigitalSubmissionMeta,
  fetchSurveySubmissionMeta,
  finalizeSurveySubmissionMeta,
  showErrorNotification,
  optOutFromSection,
  setLoading,
  resetDigitalSurvey,
} from '../redux/actions';
import {
  selectDigitalCases,
  selectDigitalQuestionsBySection,
  selectContextQuestionBySection,
  selectSelectedCase,
  selectSelectedSection,
  selectDigitalSections,
  selectDigitalSubmissionByQuestionAndCase,
  selectDigitalSubmissionMetaBySection,
  selectContextSubmissionByQuestion,
  selectQuestionCaseSettings,
  selectSubmissionBySectionQuestionAndCase,
  selectCtxSubmissionBySectionQuestion,
  selectSettingsDictionaryByQuestionAndCase,
  selectAllDigitalSubmissionMeta,
} from '../redux/reducers/SurveyDataReducer';
import {
  DigitalSubmission,
  Enrollment,
  RootState,
  Trial,
} from '../redux/types';
import NotificationBox from '../components/layout/NotificationBox';
import QuestionnaireExhibitA from '../components/survey/questionnaire/exhibitA/QuestionnaireExhibitA';
import QuestionnaireExhibitB from '../components/survey/questionnaire/exhibitB/QuestionnaireExhibitB';
import AnswerSummaryPrint from '../components/digitalTrial/AnswerSummaryPrint';
import WholeSurveyPrint from '../components/digitalTrial/WholeSurveyPrint';
import GenericDialog from '../components/GenericDialog';
import FeedbackDialog from '../components/survey/FeedbackDialog';
import { selectIsEditorPrivileged } from '../redux/reducers/Users';

const PrintBox = styled(Box)`
  @media print {
    .hideprint {
      display: none;
    }
  }
  @media screen {
    .hidescreen {
      display: none;
    }
  }
`;

type RouteProps = {
  trialId: string,
  surveyId: string,
  sectionId: string,
  enrollmentId?: string,
}

const SurveyView: React.FC<Props> = ({
  showEnrollmentsSubmissions,
  survey,
  userRole,
  sections,
  selectedSection,
  questions,
  contextQuestions,
  cases,
  selectedCase,
  submissionsMapByQuestionAndCaseId,
  contextSubmissionMapByQuestionId,
  sectionSubmissionMeta,
  surveySubmissionMeta,
  questionCaseSettings,
  fullSubmissionMap,
  fullCtxSubmissionMap,
  settingsDictionary,
  digitalSubmissionMetas,
  isEditorPrivileged,
  fetchFullDigitalSurvey: dispatchFetchFullDigitalSurvey,
  setSelectedDigitalsection: dispatchSetSelectedDigitalSection,
  setSelectedCase: dispatchSetSelectedCase,
  addOrUpdateDigitalSubmission: dispatchAddOrUpdateDigitalSubmission,
  fetchInstituteDigitalSubmissions: dispatchFetchInstituteDigitalSubmissions,
  fetchDigitalSubmissionsWithMetas: dispatchFetchDigitalSubmissionsWithMeta,
  setDigitalSubmissionMeta: dispatchSetDigitalSubmissionMeta,
  fetchSurveySubmissionMeta: dispatchFetchSurveySubmissionMeta,
  finalizeSurveySubmissionMeta: dispatchFinalizeSurveySubmissionMeta,
  optOutFromSection: dispatchOptOutFromSection,
  setLoading: dispatchSetLoading,
  showErrorNotification: dispatchShowErrorNotification,
  resetDigitalSurvey: dispatchResetDigitalSurvey,
  intl,
}: Props) => {
  const { trialId, enrollmentId } = useParams<RouteProps>();
  const [searchParams] = useSearchParams();
  const isPublicView = searchParams.get('public') === 'true';
  const [trial, setTrial] = useState<Trial>();
  const [enrollment, setEnrollment] = useState<Enrollment>();
  const [hasEnrollmentFetched, setHasEnrollmentFetched] = useState<boolean>(false);
  const [showContextQuestion, setShowContextQuestion] = useState<boolean>(false);
  const [viewType, setViewType] = useState<'exhibitA' | 'exhibitB'>('exhibitB');
  const [showPrintMode, setShowPrintMode] = useState<boolean>(false);
  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState<boolean>(false);
  const answerSummaryRef = useRef(null);
  const wholeSurveyRef = useRef(null);

  const disabledSurveyInput = sectionSubmissionMeta?.submissionFinalized
    || showEnrollmentsSubmissions
    || sectionSubmissionMeta?.isOptOut;
  const publicView = (userRole === 'admin' && !showEnrollmentsSubmissions) || isPublicView;

  useEffect(() => {
    const loadEnrollmentAndSubmissions = () => {
      if (showEnrollmentsSubmissions && enrollmentId) {
        ApiClient.getEnrollmentById(Number(enrollmentId))
          .then((e) => {
            setHasEnrollmentFetched(true);
            setEnrollment(e);
            dispatchFetchDigitalSubmissionsWithMeta(e.id);
          });
      } else {
        ApiClient.getInstituteEnrollmentByTrial(Number(trialId))
          .then((e) => {
            setHasEnrollmentFetched(true);
            if (!e) {
              return;
            }
            setEnrollment(e);
            dispatchFetchSurveySubmissionMeta(e.id);
            dispatchFetchInstituteDigitalSubmissions(Number(trialId));
            // initialize digitalsubmissionmeta for all sections
            ApiClient.findOrCreateSubmissionMetaForAllSections(Number(trialId))
              .then((dsms) => dispatchSetDigitalSubmissionMeta(dsms));
          });
      }
    };
    if (isPublicView) {
      // bypass set enrollment as this is a public view
      setHasEnrollmentFetched(true);
      return undefined;
    }
    ApiClient.getTrial(Number(trialId)).then((fetchedTrial) => setTrial(fetchedTrial));
    ApiClient.getDigitalSurveyByTrialId(Number(trialId))
      .then((surveys) => surveys.filter((ds) => !ds.exercise))
      .then((selectedSurvey) => dispatchFetchFullDigitalSurvey(selectedSurvey[0].id));
    loadEnrollmentAndSubmissions();

    return () => {
      // clear digital Survey
      dispatchResetDigitalSurvey();
    };
  }, [
    isPublicView,
    trialId,
    dispatchFetchFullDigitalSurvey,
    dispatchFetchInstituteDigitalSubmissions,
    dispatchSetDigitalSubmissionMeta,
    dispatchFetchSurveySubmissionMeta,
    enrollmentId,
    showEnrollmentsSubmissions,
    dispatchFetchDigitalSubmissionsWithMeta,
    dispatchResetDigitalSurvey,
  ]);

  useEffect(() => {
    if (!selectedSection && sections.length > 0) {
      dispatchSetSelectedDigitalSection(sections[0].id);
    } else {
      dispatchSetSelectedCase(undefined);
    }
  }, [selectedSection, dispatchSetSelectedDigitalSection, dispatchSetSelectedCase, sections]);

  useEffect(() => {
    setShowContextQuestion(false);
    if (selectedCase === undefined && cases.length > 0 && !showContextQuestion) {
      dispatchSetSelectedCase(cases[0].id);
    } else if (selectedSection && selectedCase === undefined && contextQuestions.length > 0) {
      setShowContextQuestion(true);
    }
  }, [
    selectedSection, selectedCase, dispatchSetSelectedCase, cases,
    contextQuestions, showContextQuestion,
  ]);

  const postFinalization = () => {
    setShowFeedbackPrompt(true);
  };

  const finalizeSubmissions = useCallback(async () => {
    dispatchSetLoading(true);
    let errorCode;
    try {
      const promiseArr = sections.map((sec) => ApiClient
        .finalizeDigitalSubmission(Number(trialId), sec.id));
      const metas = await Promise.all(promiseArr);
      if (enrollment) {
        dispatchFinalizeSurveySubmissionMeta(enrollment.id);
      }
      dispatchSetDigitalSubmissionMeta(metas);
    } catch (e) {
      errorCode = (e as Error).message;
      showErrorNotification(errorCode);
    } finally {
      dispatchSetLoading(false);
    }

    if (errorCode) {
      let errorMessage = errorCode;
      if (errorCode === 'SUBMISSION_DATE_ENDED') {
        errorMessage = intl.messages['submission.saveFailedEndDate'];
      }
      dispatchShowErrorNotification(errorMessage);
      return;
    }

    postFinalization();
  }, [
    dispatchFinalizeSurveySubmissionMeta,
    dispatchSetDigitalSubmissionMeta,
    dispatchSetLoading,
    dispatchShowErrorNotification,
    enrollment,
    intl.messages,
    sections,
    trialId,
  ]);

  const onAddOrUpdateDigitalSubmission = useCallback((submission: DigitalSubmission) => {
    dispatchAddOrUpdateDigitalSubmission(submission);
  }, [dispatchAddOrUpdateDigitalSubmission]);

  const showErrorNotificationCallback = useCallback((message: string) => {
    dispatchShowErrorNotification(message);
  }, [dispatchShowErrorNotification]);

  if (!hasEnrollmentFetched) {
    return null;
  }
  if ((userRole !== 'admin' && !enrollment && !isPublicView) || !survey) {
    return (
      <NotificationBox error>
        You are not allowed to access this content.
      </NotificationBox>
    );
  }

  return (
    <PrintBox>
      <Box className="hideprint">
        <Toolbar sx={{ bgcolor: 'primary.main', py: 1.5 }}>
          <Typography variant="h5" flexGrow={1} color="primary.contrastText">
            {trial?.name}
            {
              userRole === 'admin' && !publicView && (
                `   --- (Set Number: ${enrollment?.sampleset || 'N/A'})`
              )
            }
          </Typography>
          <Box display="flex" gap={2}>
            <Tooltip title={<FormattedMessage id="enrollmentsmanager.detailedEvaluationPrintButton" />}>
              <IconButton sx={{ color: 'primary.contrastText', bgcolor: 'primary.dark' }} onClick={() => setShowPrintMode(true)}>
                <PrintIcon sx={{ fontSize: '1.5em' }} />
              </IconButton>
            </Tooltip>
            <IconButton sx={{ color: 'primary.contrastText', bgcolor: 'primary.dark' }} onClick={() => setViewType(viewType === 'exhibitA' ? 'exhibitB' : 'exhibitA')}>
              {
                viewType === 'exhibitA' ? <GridOnIcon sx={{ fontSize: '1.5em' }} /> : <ViewListIcon sx={{ fontSize: '1.5em' }} />
              }
            </IconButton>
          </Box>
        </Toolbar>
        {
          viewType === 'exhibitA' && (
            <QuestionnaireExhibitA
              trial={trial}
              showEnrollmentsSubmissions={showEnrollmentsSubmissions}
              userRole={userRole}
              selectedCase={selectedCase}
              cases={cases}
              questions={questions}
              contextQuestions={contextQuestions}
              selectedSection={selectedSection}
              sections={sections}
              submissionsMapByQuestionAndCaseId={submissionsMapByQuestionAndCaseId}
              contextSubmissionMapByQuestionId={contextSubmissionMapByQuestionId}
              sectionSubmissionMeta={sectionSubmissionMeta}
              surveySubmissionMeta={surveySubmissionMeta}
              questionCaseSettings={questionCaseSettings}
              settingsDictionary={settingsDictionary}
              fullSubmissionMap={fullSubmissionMap}
              fullCtxSubmissionMap={fullCtxSubmissionMap}
              digitalSubmissionMetas={digitalSubmissionMetas}
              showContextQuestion={showContextQuestion}
              enrollment={enrollment}
              isPublicView={publicView}
              setShowContextQuestion={setShowContextQuestion}
              optOutFromSection={dispatchOptOutFromSection}
              onAddOrUpdateDigitalSubmission={onAddOrUpdateDigitalSubmission}
              setSelectedDigitalSection={dispatchSetSelectedDigitalSection}
              setSelectedCase={dispatchSetSelectedCase}
              finalizeSubmissions={finalizeSubmissions}
              disabled={!!disabledSurveyInput}
              isEditorPrivileged={isEditorPrivileged}
              showErrorNotification={showErrorNotificationCallback}
            />
          )
        }
        {
          viewType === 'exhibitB' && (
            <QuestionnaireExhibitB
              trial={trial}
              showEnrollmentsSubmissions={showEnrollmentsSubmissions}
              enrollment={enrollment}
              cases={cases}
              questions={questions}
              contextQuestions={contextQuestions}
              selectedSection={selectedSection}
              sections={sections}
              submissionsMapByQuestionAndCaseId={submissionsMapByQuestionAndCaseId}
              contextSubmissionMapByQuestionId={contextSubmissionMapByQuestionId}
              settingsDictionary={settingsDictionary}
              fullSubmissionMap={fullSubmissionMap}
              fullCtxSubmissionMap={fullCtxSubmissionMap}
              questionCaseSettings={questionCaseSettings}
              dsms={digitalSubmissionMetas}
              sectionSubmissionMeta={sectionSubmissionMeta}
              setSelectedDigitalSection={dispatchSetSelectedDigitalSection}
              onAddOrUpdateDigitalSubmission={onAddOrUpdateDigitalSubmission}
              finalizeSubmissions={finalizeSubmissions}
              optOutFromSection={dispatchOptOutFromSection}
              disabled={!!disabledSurveyInput}
              isPublicView={publicView}
              isEditorPrivileged={isEditorPrivileged}
              showErrorNotification={showErrorNotificationCallback}
            />
          )
        }
      </Box>
      <Box className="hidescreen">
        {
          enrollment && (
            <AnswerSummaryPrint
              trialId={Number(trialId)}
              enrollmentId={enrollment.id}
              onRef={answerSummaryRef}
            />
          )
        }
        <WholeSurveyPrint onRef={wholeSurveyRef} />
      </Box>
      {/* Print Dialog */}
      <GenericDialog
        isOpen={showPrintMode}
        maxWidth="sm"
        onRequestClose={() => setShowPrintMode(false)}
      >
        <Box sx={{ p: '1em' }}>
          <Typography variant="h6"><FormattedMessage id="print.choosePrintMode" /></Typography>
          <Box display="flex" gap={2} sx={{ mt: '1em' }}>
            <ReactToPrint
              trigger={() => (
                <Button variant="contained" onClick={() => window.print()}>
                  <FormattedMessage id="print.printSummary" />
                </Button>
              )}
              content={() => answerSummaryRef.current}
            />
            <ReactToPrint
              trigger={() => (
                <Button variant="contained" onClick={() => window.print()}>
                  <FormattedMessage id="print.printWhole" />
                </Button>
              )}
              content={() => wholeSurveyRef.current}
            />
          </Box>
        </Box>
      </GenericDialog>
      {/* Feedback Prompt */}
      <GenericDialog isOpen={showFeedbackPrompt && !!surveySubmissionMeta}>
        {
          survey && (
            <FeedbackDialog
              surveyId={survey.id}
              surveySubmissionMetaId={surveySubmissionMeta?.id}
              trialName={trial?.name}
              trialVersion={trial?.version}
              onClose={() => setShowFeedbackPrompt(false)}
            />
          )
        }
      </GenericDialog>
    </PrintBox>
  );
};

const mapStateToProps = (state: RootState, ownProps: OwnProps) => ({
  // showSubmissions is for admin who views user submissions
  showEnrollmentsSubmissions: ownProps.showEnrollmentsSubmissions,
  userRole: state.users.token?.role,
  survey: state.SurveyData.survey,
  selectedCase: selectSelectedCase(state),
  cases: selectDigitalCases(state),
  questions: selectDigitalQuestionsBySection(state),
  contextQuestions: selectContextQuestionBySection(state),
  selectedSection: selectSelectedSection(state),
  sections: selectDigitalSections(state),
  submissionsMapByQuestionAndCaseId: selectDigitalSubmissionByQuestionAndCase(state),
  contextSubmissionMapByQuestionId: selectContextSubmissionByQuestion(state),
  sectionSubmissionMeta: selectDigitalSubmissionMetaBySection(state),
  surveySubmissionMeta: state.SurveyData.surveySubmissionMeta,
  questionCaseSettings: selectQuestionCaseSettings(state),
  settingsDictionary: selectSettingsDictionaryByQuestionAndCase(state),
  fullSubmissionMap: selectSubmissionBySectionQuestionAndCase(state),
  fullCtxSubmissionMap: selectCtxSubmissionBySectionQuestion(state),
  digitalSubmissionMetas: selectAllDigitalSubmissionMeta(state),
  isEditorPrivileged: selectIsEditorPrivileged(state),
});

const dispatchProps = {
  fetchFullDigitalSurvey,
  setSelectedDigitalsection,
  setSelectedCase,
  addOrUpdateDigitalSubmission,
  fetchInstituteDigitalSubmissions,
  fetchDigitalSubmissionsWithMetas,
  setDigitalSubmissionMeta,
  fetchSurveySubmissionMeta,
  finalizeSurveySubmissionMeta,
  showErrorNotification,
  optOutFromSection,
  setLoading,
  resetDigitalSurvey,
};
const connector = connect(mapStateToProps, dispatchProps);
type OwnProps = { showEnrollmentsSubmissions?: boolean };
type Props = OwnProps & ConnectedProps<typeof connector> & { intl: IntlState };
export default connector(injectIntl(SurveyView));
